import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, ButtonBase, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useAgentSectionCard } from './useAgentSectionCard';
import { type AgentSection } from '../../state/createProjectSlice';

// TODO: cleanup with cp_annotation_createproject_agent ff

// Same asterisk colour as the other mandatory fields in the wizard.
const REQUIRED_FIELD_SX = { '& .MuiFormLabel-asterisk': { color: 'error.main' } };

interface AgentSectionCardProps {
  id: string;
  section: AgentSection | undefined;
  onNewSectionResolved: (id: string) => void;
}

/** Step 5 custom section card, styled like the concept cards. */
export const AgentSectionCard = ({ id, section, onNewSectionResolved }: AgentSectionCardProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const {
    isExpanded,
    draft,
    headerLabel,
    showHeadingError,
    showBodyError,
    handleToggleExpanded,
    handleDraftChange,
    handleSave,
    handleCancel,
    handleRemove,
  } = useAgentSectionCard({ id, section, onNewSectionResolved });

  const idPrefix = `agent-section-${id}`;
  const bodyId = `${idPrefix}-body`;
  const title =
    headerLabel ??
    formatMessage({ id: 'createProjectWizard.step.annotationAgent.section.untitled' });

  return (
    <Box
      id={idPrefix}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: `${borderRadius.md}px`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
        {/* The remove button is a sibling of the toggle, so a click on it never toggles. */}
        <ButtonBase
          id={`${idPrefix}-toggle`}
          onClick={handleToggleExpanded}
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          sx={{
            flex: 1,
            minWidth: 0,
            justifyContent: 'flex-start',
            gap: theme.spacing(2),
            p: theme.spacing(4),
            textAlign: 'left',
            borderRadius: `${borderRadius.md}px`,
          }}
        >
          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography variant="body1" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {title}
          </Typography>
        </ButtonBase>
        <IconButton
          id={`${idPrefix}-remove-button`}
          size="small"
          onClick={handleRemove}
          aria-label={formatMessage(
            { id: 'createProjectWizard.step.annotationAgent.section.remove' },
            { name: title }
          )}
          sx={{ mr: theme.spacing(4), color: theme.palette.error.main }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      {isExpanded && draft && (
        <Box
          id={bodyId}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            px: theme.spacing(4),
            pt: theme.spacing(4),
            pb: theme.spacing(5),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(4),
          }}
        >
          <TextField
            id={`${idPrefix}-heading`}
            fullWidth
            required
            size="small"
            label={formatMessage({
              id: 'createProjectWizard.step.annotationAgent.section.field.heading',
            })}
            value={draft.heading}
            onChange={(event) => handleDraftChange({ heading: event.target.value })}
            sx={REQUIRED_FIELD_SX}
            error={showHeadingError}
            helperText={
              showHeadingError
                ? formatMessage({
                    id: 'createProjectWizard.step.annotationAgent.section.heading.required',
                  })
                : undefined
            }
          />
          <TextField
            id={`${idPrefix}-body-field`}
            fullWidth
            required
            multiline
            minRows={3}
            size="small"
            label={formatMessage({
              id: 'createProjectWizard.step.annotationAgent.section.field.body',
            })}
            value={draft.body}
            onChange={(event) => handleDraftChange({ body: event.target.value })}
            sx={REQUIRED_FIELD_SX}
            error={showBodyError}
            helperText={
              showBodyError
                ? formatMessage({
                    id: 'createProjectWizard.step.annotationAgent.section.body.required',
                  })
                : undefined
            }
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(2) }}>
            <Button
              id={`${idPrefix}-cancel-button`}
              variant="outlined"
              displayLabel={formatMessage({
                id: 'createProjectWizard.step.annotationAgent.section.cancel',
              })}
              onClickHandler={handleCancel}
              sx={{ textTransform: 'none' }}
            />
            <Button
              id={`${idPrefix}-save-button`}
              variant="contained"
              displayLabel={formatMessage({
                id: 'createProjectWizard.step.annotationAgent.section.save',
              })}
              onClickHandler={handleSave}
              sx={{ textTransform: 'none', backgroundColor: theme.palette.primary.main }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
